/**
 * AdminRentalListingsPanel — global CRUD over every seller's rental
 * product config. Mounted inside AdminRentalsTab below the master
 * switch + per-seller toggle cards.
 *
 * Capabilities:
 *   • List all rental products with seller + store enrichment.
 *   • Search by product name, scope to a seller, filter by status.
 *   • Inline edit per-day / deposit / min-max days + Activate/Deactivate.
 *   • Delete the rental config (product survives, fields wiped).
 *   • Add a rental config to a non-rentable product (via search picker).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Search, Pencil, Trash2, Power, PowerOff, Plus, X, Save } from "lucide-react";
import { api, formatApiError } from "../lib/api";


function EditRow({ row, onSaved, onCancel, flash }) {
  const [cfg, setCfg] = useState({
    rental_enabled: !!row.rental_enabled,
    rental_per_day: row.rental_per_day || "",
    rental_deposit: row.rental_deposit || "",
    rental_min_days: row.rental_min_days || 1,
    rental_max_days: row.rental_max_days || 30,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/rentals/products/${row.product_id}`, {
        rental_enabled: cfg.rental_enabled,
        rental_per_day: cfg.rental_per_day ? parseFloat(cfg.rental_per_day) : 0,
        rental_deposit: cfg.rental_deposit ? parseFloat(cfg.rental_deposit) : 0,
        rental_min_days: parseInt(cfg.rental_min_days || 1),
        rental_max_days: parseInt(cfg.rental_max_days || 30),
      });
      flash("Rental config saved.");
      onSaved();
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Save failed", true);
    } finally { setBusy(false); }
  };
  return (
    <tr className="bg-[#0F0817]"
      data-testid={`admin-rental-edit-${row.product_id}`}>
      <td colSpan={6} className="p-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Per day $</span>
            <input type="number" min="0" step="1" value={cfg.rental_per_day}
              data-testid={`admin-rental-perday-${row.product_id}`}
              onChange={e => set("rental_per_day", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Deposit $</span>
            <input type="number" min="0" step="1" value={cfg.rental_deposit}
              onChange={e => set("rental_deposit", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Min days</span>
            <input type="number" min="1" max="365" value={cfg.rental_min_days}
              onChange={e => set("rental_min_days", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Max days</span>
            <input type="number" min="1" max="365" value={cfg.rental_max_days}
              onChange={e => set("rental_max_days", e.target.value)}
              className="input-dark text-xs w-full"/>
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={cfg.rental_enabled}
              data-testid={`admin-rental-enabled-${row.product_id}`}
              onChange={e => set("rental_enabled", e.target.checked)}/>
            <span className="font-bold uppercase tracking-widest text-[10px]">Enabled</span>
          </label>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy}
            data-testid={`admin-rental-edit-cancel-${row.product_id}`}
            className="btn-secondary text-xs px-3 py-1.5"><X size={11} className="inline mr-1"/>Cancel</button>
          <button onClick={save} disabled={busy}
            data-testid={`admin-rental-edit-save-${row.product_id}`}
            className="btn-primary text-xs px-3 py-1.5"><Save size={11} className="inline mr-1"/>{busy ? "Saving…" : "Save"}</button>
        </div>
      </td>
    </tr>
  );
}


export default function AdminRentalListingsPanel({ flash }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.search = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/rentals/products", { params });
      setRows(data || []);
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Load failed", true);
    } finally { setLoading(false); }
  }, [q, statusFilter, flash]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (row) => {
    setBusyId(row.product_id);
    try {
      await api.patch(`/admin/rentals/products/${row.product_id}`, {
        rental_enabled: !row.rental_enabled,
        rental_per_day: row.rental_per_day || 1,
        rental_deposit: row.rental_deposit || 0,
        rental_min_days: row.rental_min_days,
        rental_max_days: row.rental_max_days,
      });
      flash(`${row.name}: rental ${!row.rental_enabled ? "enabled" : "disabled"}.`);
      load();
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Toggle failed", true);
    } finally { setBusyId(null); }
  };

  const deleteConfig = async (row) => {
    if (!window.confirm(`Remove rental config from "${row.name}"? Existing reservations are kept; the product just won't be rentable anymore.`)) return;
    setBusyId(row.product_id);
    try {
      await api.delete(`/admin/rentals/products/${row.product_id}`);
      flash(`Removed rental from ${row.name}.`);
      load();
    } catch (e) {
      flash(formatApiError(e?.response?.data?.detail) || "Delete failed", true);
    } finally { setBusyId(null); }
  };

  return (
    <div className="card p-4" data-testid="admin-rental-listings">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="font-display font-bold uppercase text-sm">Rental listings · platform-wide</div>
        <div className="text-xs text-[#A1A1AA] flex-1">{rows.length} products</div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          data-testid="admin-rental-status-filter"
          className="input-dark text-xs">
          <option value="">All statuses</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-2.5 text-[#A1A1AA]"/>
          <input value={q} onChange={e => setQ(e.target.value)}
            data-testid="admin-rental-search"
            placeholder="Search product name…"
            className="input-dark pl-8 pr-3 py-2 text-xs w-full"/>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[#A1A1AA] py-8">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-sm text-[#A1A1AA] py-8">No rental products match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold">
              <tr className="border-b border-[#3D2845]">
                <th className="text-left py-2 pr-3">Product</th>
                <th className="text-left py-2 pr-3">Seller / store</th>
                <th className="text-right py-2 pr-3">$/day · Deposit</th>
                <th className="text-right py-2 pr-3">Min / Max</th>
                <th className="text-center py-2 pr-3">Status</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3D2845]">
              {rows.map(r => {
                const isEditing = editingId === r.product_id;
                const busy = busyId === r.product_id;
                return (
                  <React.Fragment key={r.product_id}>
                    <tr data-testid={`admin-rental-row-${r.product_id}`}>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <img src={r.image} className="w-8 h-8 rounded-lg object-cover shrink-0" alt=""/>
                          <div className="min-w-0">
                            <div className="font-bold truncate">{r.name}</div>
                            <div className="text-[10px] text-[#A1A1AA]">${r.price} list</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-bold truncate">{r.store_name}</div>
                        <div className="text-[10px] text-[#A1A1AA] truncate">{r.seller_email}</div>
                        {r.seller_rentals_enabled === false && (
                          <div className="text-[9px] uppercase font-bold tracking-widest text-[#FF007F] mt-0.5">Seller rentals off</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <div className="font-bold text-[#D4FF00]">${r.rental_per_day || "—"}/day</div>
                        <div className="text-[10px] text-[#A1A1AA]">Dep ${r.rental_deposit || 0}</div>
                      </td>
                      <td className="py-2 pr-3 text-right text-[#A1A1AA]">
                        {r.rental_min_days} / {r.rental_max_days}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded ${
                          r.rental_enabled ? "bg-[#D4FF00]/15 text-[#D4FF00]"
                                           : "bg-[#A1A1AA]/15 text-[#A1A1AA]"
                        }`}>{r.rental_enabled ? "Enabled" : "Disabled"}</span>
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <button onClick={() => setEditingId(isEditing ? null : r.product_id)}
                          data-testid={`admin-rental-edit-${r.product_id}`}
                          title="Edit fields"
                          className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black inline-flex items-center justify-center mr-1">
                          <Pencil size={11}/>
                        </button>
                        <button onClick={() => toggleEnabled(r)} disabled={busy}
                          data-testid={`admin-rental-toggle-${r.product_id}`}
                          title={r.rental_enabled ? "Deactivate" : "Activate"}
                          className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FFA500] hover:text-black inline-flex items-center justify-center mr-1">
                          {r.rental_enabled ? <PowerOff size={11}/> : <Power size={11}/>}
                        </button>
                        <button onClick={() => deleteConfig(r)} disabled={busy}
                          data-testid={`admin-rental-delete-${r.product_id}`}
                          title="Remove rental config"
                          className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white inline-flex items-center justify-center">
                          <Trash2 size={11}/>
                        </button>
                      </td>
                    </tr>
                    {isEditing && (
                      <EditRow row={r}
                        flash={flash}
                        onSaved={() => { setEditingId(null); load(); }}
                        onCancel={() => setEditingId(null)}/>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
